import { ng } from "@common/ngds";
import { getClientId } from "@common/helpers/funlib";
import { IFile } from "@common/types/sharedTypes";
import {
  timberProtectionHasLayerThicknesses,
  timberProtectionHasThickness,
} from "../components/FireSettings/shared";

// Shared plumbing for the member (auto) design panels. The three material groups differ only in
// what the user gets to choose — the element/material lookups, the section library grouping and
// the assembly of the AutoDesign reference databases are the same for all of them, so they live
// here rather than in one of the panels.

export type MemberMaterialGroup = "Concrete" | "Steel" | "Timber";

/**
 * Assembles the AutoDesign payload for one run. Returning null aborts the run silently — for a
 * builder that has already told the user why it cannot proceed.
 */
export type BuildAutoDesign = () => Promise<ng.endpoint_args.common_api_functions.AutoDesign_json | null>;

/**
 * Starts a design run from a payload builder. The panel shell owns the busy state, the store
 * dispatch and the error reporting, so each material's settings only have to describe its payload.
 */
export type RunMemberDesign = (build: BuildAutoDesign) => Promise<void>;

/**
 * A section as the library list (/api/sections/list) reports it: no dimensions, only identity and
 * who owns it. `ownertype` comes straight from the asset API, so it may arrive as the numeric enum
 * or as its name — resolve it with isStandardSection rather than comparing it directly.
 */
export interface DesignSectionItem {
  guid: string;
  name: string;
  familyName: string;
  ownertype?: unknown;
}

/** Sections offered for a design run, grouped by section family ("HEA", "Glulam", …). */
export type DesignSectionFamilies = Record<string, DesignSectionItem[]>;

type FrontendProject = ng.frontend.column.Project_json;

// This app models a single column, so "the element" is always the first one of the first structure.
const primaryElement = (project: FrontendProject | null | undefined) =>
  project?.data?.input?.structures?.[0]?.elements?.[0];

export const getElementGuid = (project: FrontendProject | null | undefined): string =>
  primaryElement(project)?.id?.guid ?? "";

export const getElementMaterialGuid = (project: FrontendProject | null | undefined): string =>
  primaryElement(project)?.beam?.mtrlGuid ?? "";

export const getElementSectionGuid = (project: FrontendProject | null | undefined): string =>
  primaryElement(project)?.beam?.segments?.[0]?.layer?.start?.secGuid ?? "";

/** The element's own material, from the project's input database. */
export const getElementMaterial = (
  project: FrontendProject | null | undefined,
): ng.material.Data_json | undefined => {
  const guid = getElementMaterialGuid(project);
  if (!guid) return undefined;
  return project?.data?.input?.mtrlDb?.find((m) => m?.id?.guid === guid);
};

/**
 * Which design service the column belongs to, derived from the element's material. Null when the
 * material cannot be resolved yet (the project data has not loaded), which callers treat as
 * "nothing to show yet" rather than defaulting to a group and offering the wrong inputs.
 */
export const getMemberMaterialGroup = (
  project: FrontendProject | null | undefined,
): MemberMaterialGroup | null => {
  const material = getElementMaterial(project);
  if (!material?.type) return null;
  switch (ng.material.Mtrl_FromObject(material.type)) {
    case ng.material.Mtrl.MTRL_CONCRETE:
      return "Concrete";
    case ng.material.Mtrl.MTRL_STEEL:
      return "Steel";
    case ng.material.Mtrl.MTRL_TIMBER:
      return "Timber";
    default:
      return null;
  }
};

/**
 * Whether the element has a fire protection material assigned. Only then is there a protection
 * thickness for a design run to choose, so only then are its limits worth asking the user for.
 *  - Steel names its protection material by guid, with "none_" for an unprotected member. A steel
 *    member can instead be checked against a maximum temperature, which assigns no material and so
 *    has no thickness to design — that arm carries no protectingMaterial, so it reads as false here
 *    and is picked up by hasSteelFireProtectionTemperature instead.
 *  - Timber picks from a fixed list of protection materials, with MATERIAL_TYPE_NONE for an
 *    unprotected member. A user-defined protection is described by its charring parameters rather
 *    than by a thickness, so it too has nothing for a run to design and reads as false here.
 */
export const hasFireProtectionMaterial = (
  project: FrontendProject | null | undefined,
  group: MemberMaterialGroup,
): boolean => {
  const designSettings = primaryElement(project)?.designSettings;

  if (group === "Steel") {
    const guid = designSettings?.steel?.fire?.protectingMaterial?.guid ?? "";
    return guid !== "" && guid !== "none_";
  }

  if (group === "Timber") {
    const materialType = designSettings?.timber?.fire?.protection?.mtrl?.properties?.materialType;
    if (materialType == null) return false;
    const resolved = ng.fire_protection.timber.MaterialType_FromObject(materialType);
    return timberProtectionHasThickness(resolved) || timberProtectionHasLayerThicknesses(resolved);
  }

  return false;
};

/**
 * Whether a steel element is checked against a maximum member temperature instead of being
 * protected by a material. This is the other arm of the same oneof as the protection material, so
 * at most one of this and hasFireProtectionMaterial holds. What a design run varies for such a
 * member is the temperature it searches with, not a protection thickness.
 */
export const hasSteelFireProtectionTemperature = (
  project: FrontendProject | null | undefined,
  group: MemberMaterialGroup,
): boolean => {
  if (group !== "Steel") return false;
  return primaryElement(project)?.designSettings?.steel?.fire?.temperature != null;
};

/** The timber product type (regular / glulam / kerto / …) of the element's material. */
export const getTimberProductType = (
  project: FrontendProject | null | undefined,
): ng.material.timber.ProductType => {
  const product = getElementMaterial(project)?.timberData?.product;
  if (product == null) return ng.material.timber.ProductType.PRODUCT_TYPE_UNSPECIFIED;
  return ng.material.timber.ProductType_FromObject(product);
};

// Timber sections and timber materials are not freely combinable: rectangular, sawn and dressed
// lumber all share the regular-timber materials, while glulam and kerto each have their own. The
// element's material therefore decides which section families may take part in a design run —
// offering a Sawn section for a glulam material would produce a section/material pair the design
// service cannot check. Mirrors the product-type switch in sx-beam's NGAutoDesign.
const TIMBER_FAMILIES_BY_PRODUCT: Partial<Record<ng.material.timber.ProductType, string[]>> = {
  [ng.material.timber.ProductType.PRODUCT_TYPE_REGULAR]: ["R", "Sawn", "Dressed"],
  [ng.material.timber.ProductType.PRODUCT_TYPE_GL]: ["Glulam"],
  [ng.material.timber.ProductType.PRODUCT_TYPE_KERTO]: ["Kerto"],
};

/** Section families a timber material of this product type may be paired with. */
export const getTimberSectionFamilies = (product: ng.material.timber.ProductType): string[] =>
  TIMBER_FAMILIES_BY_PRODUCT[product] ?? [];

// The section library keys its categories by material. Steel is split by ownership — but only
// OWNER_USER lands in "Steel-usr", so "Steel-std" still carries office- and company-owned sections,
// and timber's single category carries every owner. The category key alone therefore cannot decide
// what the design service will accept; isStandardSection does.
const SECTION_CATEGORY_KEYS: Record<MemberMaterialGroup, string[]> = {
  Concrete: ["Concrete"],
  Steel: ["Steel-std"],
  Timber: ["Timber"],
};

/**
 * Whether a design run for this material group may only draw on StruSoft's standard sections.
 * Steel and timber design in the fem-design-api resolves candidates against the standard section
 * library, so a user- (or office-/company-) defined section makes the run fail. Concrete design
 * takes the section geometry it is handed and so copes with sections of any ownership.
 */
const STANDARD_SECTIONS_ONLY: Record<MemberMaterialGroup, boolean> = {
  Concrete: false,
  Steel: true,
  Timber: true,
};

/** Whether a library section is one of StruSoft's standard ones, i.e. not user/office/company defined. */
export const isStandardSection = (section: DesignSectionItem): boolean =>
  ng.utils.Owner_FromObject(section?.ownertype) === ng.utils.Owner.OWNER_STRUSOFT;

/**
 * The section library (materialSectionSlice's `allSections`) grouped by family, for one material
 * group. `allowedFamilies`, when given, restricts the result to those families — used for the
 * timber section/material restriction above. Steel and timber additionally keep only standard
 * sections (see STANDARD_SECTIONS_ONLY). Families that end up empty are dropped, so a caller can
 * present the keys directly as the choice of section types.
 */
export const getDesignSectionFamilies = (
  allSections: Record<string, unknown> | null | undefined,
  group: MemberMaterialGroup,
  allowedFamilies?: string[],
): DesignSectionFamilies => {
  const families: DesignSectionFamilies = {};
  const standardOnly = STANDARD_SECTIONS_ONLY[group];

  for (const categoryKey of SECTION_CATEGORY_KEYS[group]) {
    const category = (allSections?.[categoryKey] ?? {}) as Record<string, DesignSectionItem[]>;
    // A group can span several categories, so concatenate rather than overwrite.
    for (const [familyName, sections] of Object.entries(category)) {
      if (!Array.isArray(sections) || sections.length === 0) continue;
      if (allowedFamilies && !allowedFamilies.includes(familyName)) continue;
      const offered = standardOnly ? sections.filter(isStandardSection) : sections;
      // A family the user has only non-standard sections in is not a choice we can offer.
      if (offered.length === 0) continue;
      families[familyName] = [...(families[familyName] ?? []), ...offered];
    }
  }

  return families;
};

/** Every section in a family grouping, flattened. */
export const flattenSectionFamilies = (families: DesignSectionFamilies): DesignSectionItem[] =>
  Object.values(families).flat();

/**
 * Parse y/z dimensions in mm out of a section name. The section library list carries no
 * dimensions, only names, so this is the only dimension source available without fetching every
 * section individually. Reliable for rectangular/square concrete and timber sections, whose names
 * are the dimensions ("190x450", "300"); rolled steel profile names ("HEA 220") do not encode
 * their dimensions, which is why the steel panel offers no size range.
 */
export const parseSectionDimensions = (name: string): { y: number; z: number } | null => {
  const rectangularMatch = name.match(/(\d+(?:[.,]\d+)?)\s*[xX×]\s*(\d+(?:[.,]\d+)?)/);
  if (rectangularMatch) {
    const y = Number(rectangularMatch[1].replace(",", "."));
    const z = Number(rectangularMatch[2].replace(",", "."));
    if (Number.isFinite(y) && Number.isFinite(z)) {
      return { y, z };
    }
  }

  // Square section labels can be a single size, for example "300" or "300 mm".
  const numericTokens = name.match(/\d+(?:[.,]\d+)?/g);
  if (numericTokens?.length === 1) {
    const size = Number(numericTokens[0].replace(",", "."));
    if (Number.isFinite(size)) {
      return { y: size, z: size };
    }
  }

  return null;
};

/**
 * How many sections one design run may span. The design services are exercised with at most this
 * many candidates per run, and each one costs a full code check, so a wider net mostly buys a
 * timeout. Matches sx-beam's limit.
 */
export const MAX_DESIGN_SECTIONS = 25;

/** The AutoDesign settings cached from the previous run, if any. */
export const getCachedAutoDesign = (
  project: IFile | null | undefined,
): ng.endpoint_args.common_api_functions.AutoDesign_json | undefined =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ((project?.data as any)?.settings?.endpointCache as Record<string, unknown> | undefined)?.autoDesign as
    | ng.endpoint_args.common_api_functions.AutoDesign_json
    | undefined;

/** The cached settings for the single element this app designs. */
export const getCachedAutoDesignSettings = (
  project: IFile | null | undefined,
): ng.endpoint_args.common_api_functions.AutoDesignSettings_json | undefined =>
  getCachedAutoDesign(project)?.settings?.[0];

/**
 * Collect the full section and material data a design run must carry, from the cheapest source
 * first: the AutoDesign cache of the previous run, then the project's own input databases (the
 * active section and every material the element references are always there), and only then the
 * single-item endpoints. Entries no longer referenced by this run are pruned, so the cache does
 * not accumulate every section the user has ever ranged over.
 */
export const collectDesignDatabases = async (args: {
  project: IFile | null | undefined;
  token: string | null;
  sectionGuids: string[];
  materialGuids: string[];
}): Promise<{ secDb: ng.sections.Section_json[]; mtrlDb: ng.material.Data_json[] }> => {
  const { project, token, sectionGuids, materialGuids } = args;

  const cached = getCachedAutoDesign(project);
  const cachedMtrlDb = cached?.mtrlDb ?? [];
  const cachedSecDb = cached?.secDb ?? [];

  const cachedMtrlGuids = new Set(cachedMtrlDb.map((m) => m.id?.guid).filter(Boolean));
  const cachedSecGuids = new Set(cachedSecDb.map((s) => s.id?.guid).filter(Boolean));

  const missingMtrlGuids = materialGuids.filter((g) => !cachedMtrlGuids.has(g));
  const missingSecGuids = sectionGuids.filter((g) => !cachedSecGuids.has(g));

  const inputMtrlDb = (project?.data?.data?.input?.mtrlDb ?? []) as ng.material.Data_json[];
  const inputSecDb = (project?.data?.data?.input?.secDb ?? []) as ng.sections.Section_json[];
  const inputMtrlByGuid = new Map(
    inputMtrlDb.map((m) => [m.id?.guid, m]).filter(([g]) => !!g) as [string, ng.material.Data_json][],
  );
  const inputSecByGuid = new Map(
    inputSecDb.map((s) => [s.id?.guid, s]).filter(([g]) => !!g) as [string, ng.sections.Section_json][],
  );

  const stillMissingMtrlGuids = missingMtrlGuids.filter((g) => !inputMtrlByGuid.has(g));
  const stillMissingSecGuids = missingSecGuids.filter((g) => !inputSecByGuid.has(g));

  const headers = {
    authorization: token ? `Bearer ${token}` : "",
    "Content-Type": "application/json",
    "x-client-id": getClientId(),
  };

  // Fetch what is left one item at a time — more reliable than the multi-guid endpoint.
  const [freshMtrlResults, freshSecResults] = await Promise.all([
    Promise.all(
      stillMissingMtrlGuids.map(async (g) => {
        const resp = await fetch(`/api/materials/${g}`, { method: "GET", headers });
        if (!resp.ok) return null;
        const json = await resp.json();
        // The single-material endpoint answers RestMaterial: { data: ConversionValues_json { dbMtrl } }
        return (json?.data?.dbMtrl as ng.material.Data_json) ?? null;
      }),
    ),
    Promise.all(
      stillMissingSecGuids.map(async (g) => {
        const resp = await fetch(`/api/sections/${g}`, { method: "GET", headers });
        if (!resp.ok) return null;
        return (await resp.json()) as ng.sections.Section_json | null;
      }),
    ),
  ]);

  const freshMtrlData = freshMtrlResults.filter((m): m is ng.material.Data_json => m !== null);
  const freshSecData = freshSecResults.filter((s): s is ng.sections.Section_json => s !== null);

  const wantedMtrlGuids = new Set(materialGuids);
  const wantedSecGuids = new Set(sectionGuids);

  const mtrlDb = [
    ...cachedMtrlDb.filter((m) => m.id?.guid && wantedMtrlGuids.has(m.id.guid)),
    ...missingMtrlGuids.filter((g) => inputMtrlByGuid.has(g)).map((g) => inputMtrlByGuid.get(g)!),
    ...freshMtrlData,
  ];
  const secDb = [
    ...cachedSecDb.filter((s) => s.id?.guid && wantedSecGuids.has(s.id.guid)),
    ...missingSecGuids.filter((g) => inputSecByGuid.has(g)).map((g) => inputSecByGuid.get(g)!),
    ...freshSecData,
  ];

  return { secDb, mtrlDb };
};

/**
 * A copy of the project file carrying `autoDesign` in its endpoint cache — what the /design route
 * reads its settings from. The panels dispatch setAutoDesign for the store; this is the payload
 * the request itself needs, built without waiting for the store to round-trip.
 */
export const withAutoDesign = (
  project: IFile,
  autoDesign: ng.endpoint_args.common_api_functions.AutoDesign_json,
): IFile => {
  const existingEndpointCache = (project.data?.settings?.endpointCache as Record<string, unknown>) ?? {};
  return {
    ...project,
    data: {
      ...project.data,
      settings: {
        ...project.data?.settings,
        endpointCache: {
          ...existingEndpointCache,
          autoDesign,
        },
      } as typeof project.data.settings,
    },
  };
};
